import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

type Mode = 'signin' | 'signup'

export function LoginPage() {
  const {
    user,
    loading,
    error,
    clearError,
    sendMagicLink,
    completeOpenSignup,
    totpRequired,
    confirmTotpChallenge,
    clearTotpChallenge,
    signInWithPasskey,
  } = useAuth()
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [linkSent, setLinkSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [code, setCode] = useState('')
  const [totpBusy, setTotpBusy] = useState(false)
  const [totpError, setTotpError] = useState<string | null>(null)
  const registeredRef = useRef(false)

  useEffect(() => {
    if (!user?.emailVerified || registeredRef.current) return
    registeredRef.current = true
    void completeOpenSignup()
  }, [user, completeOpenSignup])

  if (!loading && user?.emailVerified && !totpRequired) {
    return <Navigate to="/" replace />
  }

  async function onMagicLink(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    clearError()
    setLinkSent(false)
    try {
      await sendMagicLink(email, {
        displayName: mode === 'signup' ? displayName : undefined,
        intent: mode,
      })
      setLinkSent(true)
    } finally {
      setBusy(false)
    }
  }

  async function onVerifyTotp(e: FormEvent) {
    e.preventDefault()
    setTotpBusy(true)
    setTotpError(null)
    try {
      await confirmTotpChallenge(code.trim())
    } catch (err) {
      setTotpError(err instanceof Error ? err.message : 'Invalid code — try again')
    } finally {
      setTotpBusy(false)
    }
  }

  function onCancelTotp() {
    clearTotpChallenge()
    setCode('')
    setTotpError(null)
  }

  function switchMode(next: Mode) {
    setMode(next)
    clearError()
    setLinkSent(false)
  }

  return (
    <div className="shell login-shell">
      <header className="top compact">
        <div className="brand">
          <span className="mark">BeyondQuota</span>
          <h1>{mode === 'signup' ? 'Sign up' : 'Sign in'}</h1>
        </div>
        <p className="lede tight">
          {mode === 'signup'
            ? 'Create an operator account with your invited email.'
            : 'Passwordless sign-in — magic link or passkey.'}
        </p>
      </header>

      {totpRequired ? (
        <div className="login-card">
          <form className="login-form" onSubmit={(e) => void onVerifyTotp(e)}>
            <label className="ctl">
              <span>Authenticator code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={totpBusy}
                required
              />
            </label>
            <p className="login-hint" role="status">
              Enter the 6-digit code from your authenticator app.
            </p>
            <button type="submit" className="login-primary" disabled={totpBusy}>
              Verify code
            </button>
            <button type="button" className="ghost" onClick={onCancelTotp}>
              Cancel
            </button>
          </form>
          {totpError && (
            <p className="login-error" role="alert">
              {totpError}
            </p>
          )}
        </div>
      ) : (
        <div className="login-card">
          <div className="login-mode" role="tablist" aria-label="Account mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signin'}
              className={mode === 'signin' ? 'login-mode-active' : ''}
              onClick={() => switchMode('signin')}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signup'}
              className={mode === 'signup' ? 'login-mode-active' : ''}
              onClick={() => switchMode('signup')}
            >
              Sign up
            </button>
          </div>

          <form className="login-form" onSubmit={(e) => void onMagicLink(e)}>
            <div className="login-email-stack">
              {mode === 'signup' ? (
                <label className="ctl">
                  <span>Name</span>
                  <input
                    type="text"
                    autoComplete="name"
                    placeholder="Your name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    disabled={busy || loading}
                  />
                </label>
              ) : null}
              <label className="ctl">
                <span>Email</span>
                <input
                  type="email"
                  autoComplete="email webauthn"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy || loading}
                  required
                />
              </label>

              <div className="login-spam-block">
                <p className="login-spam-tip" role="note">
                  <strong>**Look for an email from no-reply@beyondquota.ai to continue.**</strong>
                </p>
              </div>

              <button
                type="submit"
                className="login-primary"
                disabled={busy || loading || !email.trim()}
              >
                {mode === 'signup' ? 'Email me a sign-up link' : 'Email me a sign-in link'}
              </button>
            </div>
          </form>

          {mode === 'signin' ? (
            <button
              type="button"
              className="login-secondary"
              style={{ marginTop: '0.75rem', width: '100%' }}
              disabled={busy || loading}
              onClick={() => void signInWithPasskey()}
            >
              Sign in with passkey
            </button>
          ) : null}

          {linkSent && !error && (
            <p className="login-hint" role="status">
              Link sent — open it on this device. If you enrolled an authenticator,
              you&apos;ll enter a TOTP code next.
            </p>
          )}
          {error && (
            <p className="login-error" role="alert">
              {error}
            </p>
          )}

          <p className="login-switch">
            {mode === 'signup' ? (
              <>
                Already have an account?{' '}
                <button type="button" className="linkish" onClick={() => switchMode('signin')}>
                  Sign in
                </button>
              </>
            ) : (
              <>
                First time here?{' '}
                <button type="button" className="linkish" onClick={() => switchMode('signup')}>
                  Sign up
                </button>
              </>
            )}
          </p>
        </div>
      )}
    </div>
  )
}
